import { z } from 'zod'

import { BookingPaymentType } from '../constants/booking'
import {
  InventorySort,
  InventoryTierMode,
  InventoryType,
} from '../constants/inventory'
import { feeSchema } from './pricing'
import { listQueryZodFields } from './system'
import { recurringSchema } from './recurring'
import {
  MembershipLevelMax,
  MembershipLevelMin,
  MembershipPerkType,
} from '../constants/membership'
import { DiscountType } from '../constants/pricing'
import { dbEntitySchema } from './system'

const inventoryTierModeSchema = z.nativeEnum(InventoryTierMode)

export const inventoryTierLevelSchema = z.object({
  _id: z.string(),
  id: z.string(),
  quantity: z.number().min(0).nullish(),
  price: z.number().min(0),
  fees: z.array(feeSchema).nullish(),
  minGuests: z.number().int().min(1).nullish(),
  maxGuests: z.number().int().min(1).nullish(),
  deleted: z.boolean(),
})

/**
 * Schema for tier level in update payload. _id, id, deleted are optional
 * because new levels don't have IDs (server assigns them) and existing
 * levels may omit id/deleted when updating.
 */
export const inventoryTierLevelUpdateSchema = z.object({
  _id: z.string().optional(),
  id: z.string().optional(),
  quantity: z.number().min(0).nullish(),
  price: z.number().min(0),
  fees: z.array(feeSchema).nullish(),
  minGuests: z.number().int().min(1).nullish(),
  maxGuests: z.number().int().min(1).nullish(),
  deleted: z.boolean().optional(),
})

export const inventoryTierLevelCreateSchema = inventoryTierLevelSchema
  .omit({
    deleted: true,
  })
  .extend({
    quantity: z.coerce.number().min(0).nullish(),
    minGuests: z.coerce.number().int().min(1).nullish(),
    maxGuests: z.coerce.number().int().min(1).nullish(),
  })

function resolveTierMode(mode?: InventoryTierMode | null): InventoryTierMode {
  return mode === InventoryTierMode.GUEST_COUNT
    ? InventoryTierMode.GUEST_COUNT
    : InventoryTierMode.QUANTITY
}

function resolveGuestBounds(
  level:
    | z.infer<typeof inventoryTierLevelSchema>
    | z.infer<typeof inventoryTierLevelCreateSchema>
    | z.infer<typeof inventoryTierLevelUpdateSchema>
): { minGuests: number; maxGuests: number } | null {
  const minGuests = level.minGuests == null ? NaN : Number(level.minGuests)
  const maxGuests = level.maxGuests == null ? NaN : Number(level.maxGuests)

  if (
    !Number.isInteger(minGuests) ||
    !Number.isInteger(maxGuests) ||
    Number.isNaN(minGuests) ||
    Number.isNaN(maxGuests)
  ) {
    return null
  }

  return { minGuests, maxGuests }
}

function validateTierShape(
  tier: {
    mode?: InventoryTierMode | null
    activeLevel?: string | null
    levels?: Array<
      | z.infer<typeof inventoryTierLevelSchema>
      | z.infer<typeof inventoryTierLevelCreateSchema>
      | z.infer<typeof inventoryTierLevelUpdateSchema>
    >
  },
  ctx: z.RefinementCtx
): void {
  const mode = resolveTierMode(tier.mode)
  const levels = tier.levels ?? []
  if (!levels.length) return

  if (mode === InventoryTierMode.QUANTITY) {
    levels.forEach((level, index) => {
      const quantity = Number(level.quantity ?? NaN)
      const levelId =
        '_id' in level && typeof level._id === 'string' ? level._id : undefined
      const isActiveLevelById =
        levelId != null &&
        tier.activeLevel != null &&
        levelId === tier.activeLevel
      const isImplicitActiveLevel = tier.activeLevel == null && index === 0
      const canBeSoldOutLevel = isActiveLevelById || isImplicitActiveLevel

      if (!Number.isFinite(quantity) || quantity < 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['levels', index, 'quantity'],
          message: 'Tier quantity must be at least 0.',
        })
        return
      }

      if (quantity < 1 && !canBeSoldOutLevel) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['levels', index, 'quantity'],
          message: 'Tier quantity must be at least 1.',
        })
      }
    })
    return
  }

  const ranges: Array<{ minGuests: number; maxGuests: number; index: number }> =
    []

  levels.forEach((level, index) => {
    const bounds = resolveGuestBounds(level)
    if (!bounds) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['levels', index],
        message: 'Guest tiers require minGuests and maxGuests.',
      })
      return
    }

    if (bounds.minGuests > bounds.maxGuests) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['levels', index, 'maxGuests'],
        message: 'maxGuests must be greater than or equal to minGuests.',
      })
      return
    }

    ranges.push({
      minGuests: bounds.minGuests,
      maxGuests: bounds.maxGuests,
      index,
    })
  })

  const sorted = [...ranges].sort((a, b) => a.minGuests - b.minGuests)
  for (let idx = 1; idx < sorted.length; idx += 1) {
    const prev = sorted[idx - 1]
    const current = sorted[idx]
    if (current.minGuests <= prev.maxGuests) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['levels', current.index],
        message: 'Guest tier ranges cannot overlap.',
      })
    }
  }
}

export const inventoryTierSchema = z
  .object({
    mode: inventoryTierModeSchema.nullish(),
    levels: z.array(inventoryTierLevelSchema),
    activeLevel: z.string().nullish(),
  })
  .superRefine(validateTierShape)

export const inventoryTierCreateSchema = z
  .object({
    mode: inventoryTierModeSchema.nullish(),
    levels: z.array(inventoryTierLevelCreateSchema),
    activeLevel: z.string().nullish(),
  })
  .superRefine(validateTierShape)

export const inventoryTierUpdateSchema = z
  .object({
    mode: inventoryTierModeSchema.nullish(),
    levels: z.array(inventoryTierLevelUpdateSchema),
    activeLevel: z.string().nullish(),
  })
  .superRefine(validateTierShape)

export const inventorySchema = dbEntitySchema.extend({
  venue: z.string(),
  product: z.string().nullish(),
  organization: z.string(),
  recurring: recurringSchema.nullish(),
  date: z.date(),
  enable: z.boolean().nullish(),
  isInternal: z.boolean().nullish(),
  inPersonPayments: z.boolean().nullish(),
  //NOTE: this is a percentage
  tax: z.number().min(0).max(1).nullish(),
  //NOTE: this is a percentage
  tip: z.number().min(0).max(1).nullish(),
  discountValue: z.number().min(0).nullish(),
  discountType: z.nativeEnum(DiscountType).nullish(),
  guests: z.number().min(0).nullish(),
  basePrice: z.number().min(0).nullish(),
  fastPassUrl: z.string().nullish(),
  hasTiers: z.boolean().nullish(),
  membershipLevel: z
    .number()
    .min(MembershipLevelMin)
    .max(MembershipLevelMax)
    .nullish(),
  membershipPerkType: z.nativeEnum(MembershipPerkType).optional(),
  price: z.number().min(0),
  location: z.string().nullish(),
  name: z.string().nullish(),
  venueLayoutSection: z.string().nullish(),
  syncQuantityBySection: z.boolean().nullish(),
  paymentType: z.nativeEnum(BookingPaymentType).nullish(),
  type: z.nativeEnum(InventoryType),
  depositAmount: z.number().min(0).nullish(),
  userPaysProcessing: z.boolean().nullish(),
  fee: z
    .object({
      fees: z.array(feeSchema),
      enable: z.boolean().nullish(),
    })
    .nullish(),
  fees: z.array(feeSchema).nullish(),
  /**
   * @description Canonical arrival-window start on the inventory diary date.
   * Stored as venue-local wall-clock minutes past midnight.
   */
  start: z.number(),
  /**
   * @description Canonical arrival-window end on the inventory diary date.
   * Stored as venue-local wall-clock minutes past midnight.
   */
  end: z.number(),
  /**
   * @description Sellable start time in UTC
   */
  sellingStart: z.date().nullish(),
  /**
   * @description Sellable end time in UTC
   */
  sellingEnd: z.date().nullish(),
  /**
   * @description Sellable start time in the venue timezone
   */
  sellingStartDisplay: z.date().nullish(),
  /**
   * @description Sellable end time in the venue timezone
   */
  sellingEndDisplay: z.date().nullish(),
  /**
   * @description Check in start time in the venue timezone
   */
  checkInStart: z.date().nullish(),
  /**
   * @description Check in end time in the venue timezone
   */
  checkInEnd: z.date().nullish(),
  /**
   * @deprecated Derived alias for `start`; retained for backwards compatibility.
   */
  arrivalStartTime: z.date().nullish(),
  /**
   * @deprecated Derived alias for `end`; retained for backwards compatibility.
   */
  arrivalEndTime: z.date().nullish(),
  //
  event: z.string().nullish(),
  city: z.string().nullish(),
  timezone: z.string(),
  confirmationRequest: z.string().nullish(),
  bulkUploadCode: z.string().nullish(),
  qrCodeUrl: z.string().nullish(),
  quantity: z.number().min(0),
  quantityReserved: z.number().min(0).nullish(),
  quantitySold: z.number().nullish(),
  quantityTotal: z.number().nullish(),
  purchaseMax: z.number().min(0).nullish(),
  purchaseMin: z.number().min(0).nullish(),
  quantityMin: z.number().min(0).nullish(),
  quantityMax: z.number().min(0).nullish(),
  image: z.string().nullish(),
  pdfAdImageUrl: z.string().nullish(),
  description: z.string().nullish(),
  shortDescription: z.string().nullish(),
  termsAndConditions: z.string().nullish(),
  ticket: z.boolean().nullish(),
  subType: z.string().nullish(),
  tier: inventoryTierSchema.nullish(),
})

export const inventoryListQuerySchema = z.object({
  ...listQueryZodFields,
  venueId: z.string().optional(),
  id: z.string().optional(),
  excludeId: z.string().optional(),
  search: z.string().optional(),
  enable: z.boolean().optional(),
  excludeInternal: z.boolean().optional(),
  dateStart: z.date().optional(),
  dateEnd: z.date().optional(),
  organizationId: z.string().optional(),
  cityId: z.string().optional(),
  groupId: z.string().optional(),
  eventId: z.string().optional(),
  type: z
    .nativeEnum(InventoryType)
    .or(z.array(z.nativeEnum(InventoryType)))
    .optional(),
  active: z.boolean().optional(),
  excludePaymentLinks: z.boolean().optional(),
  discounted: z.boolean().optional(),
  membershipLevelMin: z.number().optional(),
  membershipLevelMax: z.number().optional(),
  quantityMin: z.number().optional(),
  quantityMax: z.number().optional(),
  sort: z.nativeEnum(InventorySort).optional(),
})

export const inventoryCreateArgsSchema = inventorySchema
  .omit({
    _id: true,
    id: true,
    deleted: true,
    created: true,
    modified: true,
    organization: true,
    venue: true,
    event: true,
    city: true,
    venueLayoutSection: true,
    timezone: true,
    type: true,
    recurring: true,
    quantity: true,
    //selling times
    sellingStart: true,
    sellingEnd: true,
    sellingStartDisplay: true,
    sellingEndDisplay: true,
    //arrival times
    arrivalStartTime: true,
    arrivalEndTime: true,
    start: true,
    end: true,
    tier: true,
  })
  .partial()
  .extend({
    quantity: z.number().min(0).optional(),
    venueId: z.string(),
    productId: z.string().optional(),
    eventId: z.string().optional(),
    cityId: z.string().optional(),
    venueLayoutSectionId: z.string().optional(),
    /**
     * @deprecated use venueLayoutSectionId instead
     */
    venueLayoutSection: z.string().optional(),
    /**
     * @deprecated use paymentType instead
     */
    reservationEnabled: z.boolean().optional(),
    type: z.nativeEnum(InventoryType).optional(),
    recurring: recurringSchema.partial().optional(),
    //selling times
    sellingStart: z.date().nullish(),
    sellingEnd: z.date().nullish(),
    // canonical arrival-window bounds (venue-local wall clock)
    start: z.date().optional(),
    end: z.date().optional(),
    //check in times
    checkInStart: z.date().optional(),
    checkInEnd: z.date().optional(),
    tier: inventoryTierCreateSchema.optional(),
  })

export const inventoryUpdateByIdArgsSchema = inventorySchema
  .partial()
  .omit({
    _id: true,
    id: true,
    deleted: true,
    created: true,
    modified: true,
    venue: true,
    event: true,
    confirmationRequest: true,
    city: true,
    organization: true,
    recurring: true,
    timezone: true,
    venueLayoutSection: true,
    //selling times
    sellingStart: true,
    sellingEnd: true,
    sellingStartDisplay: true,
    sellingEndDisplay: true,
    //arrival times
    start: true,
    end: true,
    //check in times
    checkInStart: true,
    checkInEnd: true,
  })
  .extend({
    eventId: z.string().nullish(),
    cityId: z.string().nullish(),
    confirmationRequestId: z.string().nullish(),
    venueLayoutSectionId: z.string().nullish(),
    /**
     * @deprecated use venueLayoutSectionId instead
     */
    venueLayoutSection: z.string().nullish(),
    /**
     * @deprecated use paymentType instead
     */
    reservationEnabled: z.boolean().nullish(),
    disableRecurring: z.boolean().nullish(),
    recurring: recurringSchema.partial().nullish(),
    recurringEndDate: z.coerce.date().nullish(),
    //selling times - coerce timestamps/ISO strings from JSON body
    sellingStart: z.coerce.date().nullish(),
    sellingEnd: z.coerce.date().nullish(),
    // canonical arrival-window bounds (venue-local wall clock)
    start: z.coerce.date().nullish(),
    end: z.coerce.date().nullish(),
    //check in times
    checkInStart: z.coerce.date().nullish(),
    checkInEnd: z.coerce.date().nullish(),
    tier: inventoryTierUpdateSchema.nullish(),
    productId: z.string().nullish(),
  })

export const inventoryItemSchema = z.object({
  inventory: z.string(),
  level: z.string(),
  quantity: z.number(),
})
